import type { Connection, SaveConnectionOptions } from '../../../types/electron';
import { changedDialFields } from './dialProjection';

/**
 * True when saving `prior → next` must tear down the live engine pool (and any
 * SSH tunnel) so the next connect re-establishes with the new transport or DB
 * target.
 *
 * Pure, and shared by both layers so they can't drift:
 *   • main process — `saveConnectionHandler` withdraws the dial in flight and
 *     `closeTunnelIfEnabledFlippedOff` disconnects the pool and closes the
 *     tunnel at save time.
 *   • renderer — `connectionStore.updateConnection` drops a stale `connected`
 *     active-connection so `connectToDatabase` doesn't short-circuit the
 *     reconnect and keep running queries over the old pool.
 *
 * Fires when either half of the dial changed:
 *   • any field in the shared dial projection (`dialProjection.ts`) — the same
 *     list `dialFingerprint` uses to decide whether a registered pool may be
 *     reused. Server, port, database, engine, login, the Oracle address
 *     fields, the SSL/encrypt flags, the read-only flag and the SSH target
 *     (enabling a tunnel, disabling it, or moving it) are all in there. A
 *     brand-new connection (`prior` undefined) has no live pool, so nothing
 *     fires.
 *   • a SECRET the user edited, which no comparison can detect: the DB
 *     password is signalled by the dialog as `options.passwordEdited`, and the
 *     SSH secrets it typed travel as `options.sshPassword` /
 *     `options.sshPassphrase` — arguments of THIS save, never fields of the
 *     record (see `SaveConnectionOptions`); a `hasStored*` flip is the
 *     save-in-keychain toggle.
 */
export function connectionRequiresPoolReset(
  prior: Connection | undefined,
  next: Connection,
  options?: SaveConnectionOptions,
): boolean {
  // A brand-new connection has nothing live to reset.
  if (!prior) return false;

  // Everything the pool is DIALLED from, from the one projection the dial
  // fingerprint also reads (`dialProjection.ts`). Before that was shared, this
  // predicate listed a hand-picked subset: editing an Oracle service name, a
  // TNS alias, a wallet location, an Entra tenant or an SSL/encrypt flag left
  // the connection serving queries from a pool dialled with the old value —
  // one the fingerprint would have called stale on the very next connect.
  if (changedDialFields(prior, next).length > 0) return true;

  // The secrets, which cannot be compared and must be SIGNALLED.
  //
  //   • `options.passwordEdited` — the dialog sets it when the user actually
  //     edited the password field. It cannot be inferred: the edit form loads
  //     the DECRYPTED password, so "non-empty" is every edit of every
  //     connection that has one, and `prior` holds ciphertext against the
  //     form's plaintext, so "different" is every save. It is a save OPTION
  //     rather than a record field so that only the save the user made with
  //     the dialog carries it — a later background re-save of the same record
  //     (the post-connect `lastConnectedAt` write, say) has no options and
  //     leaves the pool alone.
  //   • `options.sshPassword` / `options.sshPassphrase` — the SSH secrets the
  //     user TYPED in this dialog session, the same class of signal (round 5,
  //     F5). They used to be read off `next.sshTunnel`, where the store kept
  //     them and every later background re-save looked like a fresh edit.
  //   • `hasStoredPassphrase` / `hasStoredPassword` flipping is a
  //     save-in-keychain toggle; in either direction the cached,
  //     already-authenticated ssh2 client is stale w.r.t. the user's intent.
  if (options?.passwordEdited === true) return true;

  const ssh = next.sshTunnel;
  if (!ssh?.enabled) return false;
  const priorAuth = prior.sshTunnel?.auth as
    | { hasStoredPassphrase?: boolean; hasStoredPassword?: boolean }
    | undefined;
  const nextAuth = ssh.auth as
    | { hasStoredPassphrase?: boolean; hasStoredPassword?: boolean }
    | undefined;
  if (options?.sshPassphrase) return true;
  if (options?.sshPassword) return true;
  if (!!priorAuth?.hasStoredPassphrase !== !!nextAuth?.hasStoredPassphrase) return true;
  if (!!priorAuth?.hasStoredPassword !== !!nextAuth?.hasStoredPassword) return true;

  return false;
}
